import { memo as Memo, useMemo, useRef, useCallback } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { FreeMode } from 'swiper';

//* GSAP
import gsap from 'gsap';

//* HOC
import withUIContext from 'context/consumerHOC/UIConsumer';

//* Components
import Text from 'components/common/Text';
import Image from 'components/common/Image';
import Video from 'components/common/Video';
import Container from 'components/common/Container';

//* Style
import GallerySliderStyle from './style';

const GallerySlider = Memo(({ data, winWidth }) => {
	//! Refs
	const sliderContRef = useRef();
	const cursorRef = useRef();

	//! Drag number responsive size
	const dragNumberSize = useMemo(() => {
		return winWidth >= 1440 ? 2 : 1;
	}, [winWidth]);

	//! Handle mouse move
	const handleMouseMove = useCallback(
		(e) => {
			if (data.length > dragNumberSize) {
				const sliderContRect = sliderContRef.current.getBoundingClientRect();
				const x = e.clientX - sliderContRect.left;
				const y = e.clientY - sliderContRect.top;

				gsap.to(cursorRef.current, { duration: 0.3, opacity: 1, left: x, top: y, scale: 1 });
			}
		},
		[data.length, dragNumberSize, cursorRef.current]
	);

	//! Handle mouse leave
	const handleMouseLeave = useCallback(() => {
		if (data.length > dragNumberSize) {
			gsap.to(cursorRef.current, { duration: 0.3, opacity: 0, scale: 0 });
		}
	}, [data.length, dragNumberSize, cursorRef.current]);

	//! Slides
	const slides = useMemo(() => {
		return data.map((item, index) => (
			<SwiperSlide key={index}>
				<div className={`gs-slide`}>
					{item.type === 'image' ? (
						<Image
							src={item?.src}
							alt={item?.alt}
							className={'contain'}
						/>
					) : (
						<Video
							src={item?.src}
							loop={true}
							autoplay={true}
						/>
					)}
				</div>
			</SwiperSlide>
		));
	}, [data]);

	return (
		<GallerySliderStyle
			dragNumberSize={dragNumberSize}
			imagesQuantity={data.length}>
			<Container>
				<div
					className={`gs-slider-container`}
					onMouseMove={handleMouseMove}
					onMouseLeave={handleMouseLeave}>
					<div
						ref={sliderContRef}
						className={`gs-slider-inner-container`}>
						<Swiper
							freeMode={true}
							draggable={true}
							modules={[FreeMode]}
							slidesPerView={'auto'}>
							{slides}
						</Swiper>

						{data.length > dragNumberSize && (
							<div
								ref={cursorRef}
								className={`gs-drag-container`}>
								<Text
									tag={`span`}
									className={`gs-drag notoSans-medium uppercase p p3`}
									text={`drag`}
								/>
							</div>
						)}
					</div>
				</div>
			</Container>
		</GallerySliderStyle>
	);
});

export default withUIContext(GallerySlider, ['winWidth']);
